import { bindInstallPrompt, isStandalone } from "./install";
import { registerServiceWorker } from "./register";
import { bootstrapAndSync, telemetry } from "./sync";
import { clearPwaData } from "./storage";
import { createPwaUi } from "./status";
import { initWebPush } from "./push";

const PWA_INSTANCE_ID = window.__CEOWEB_PWA_INSTANCE__ ||
  (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`);
window.__CEOWEB_PWA_INSTANCE__ = PWA_INSTANCE_ID;

const REFERRAL_INSTALL_COOKIE = "ceow_ref_install";
const REFERRAL_INSTALL_KEY = "ceow_ref_install_intent";
const REFERRAL_INSTALL_TTL = 7 * 24 * 60 * 60 * 1000;

const hasReferralInstallIntent = () => {
  const fromCookie = document.cookie
    .split(";")
    .some((part) => part.trim() === `${REFERRAL_INSTALL_COOKIE}=1`);
  try {
    if (fromCookie) {
      window.localStorage.setItem(REFERRAL_INSTALL_KEY, String(Date.now()));
    }
    const storedAt = Number(window.localStorage.getItem(REFERRAL_INSTALL_KEY) || 0);
    return fromCookie || (storedAt > 0 && Date.now() - storedAt < REFERRAL_INSTALL_TTL);
  } catch (error) {
    return fromCookie;
  }
};

const clearReferralInstallIntent = () => {
  document.cookie = `${REFERRAL_INSTALL_COOKIE}=; Max-Age=0; Path=/; SameSite=Lax`;
  try {
    window.localStorage.removeItem(REFERRAL_INSTALL_KEY);
  } catch (error) {
    // Storage can be unavailable in privacy mode.
  }
};

const ensureUserManifest = () => {
  if (document.querySelector('link[rel="manifest"]')) return;
  const manifest = document.createElement("link");
  manifest.rel = "manifest";
  manifest.href = "/pwa/manifest.webmanifest";
  document.head.appendChild(manifest);
};

const readConfig = () => {
  const body = document.body;
  if (!body || body.dataset.pwaEnabled !== "1") return null;
  return {
    enabled: true,
    killSwitch: body.dataset.pwaKillSwitch === "1",
    appType: body.dataset.pwaApp || "user",
    serviceWorkerUrl: body.dataset.pwaSw || "",
    scope: body.dataset.pwaScope || "/",
    bootstrapUrl: body.dataset.pwaBootstrap || "",
    snapshotUrl: body.dataset.pwaSnapshot || "",
    telemetryUrl: body.dataset.pwaTelemetry || "",
    versionUrl: (body.dataset.pwaApp || "user") === "admin"
      ? "/admin/api/pwa/version"
      : "/api/pwa/version",
  };
};

const isLogoutPath = (path) =>
  /^\/(?:user|partner|admin)\/logout\/?$/i.test(path) ||
  /^\/impersonation\/exit(?:-all)?\/?$/i.test(path);

const purgeBeforeSecurityExit = (config) => {
  document.addEventListener("click", async (event) => {
    const link = event.target?.closest?.("a[href]");
    if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey) {
      return;
    }
    let url;
    try {
      url = new URL(link.href, window.location.href);
    } catch (error) {
      return;
    }
    if (url.origin !== window.location.origin || !isLogoutPath(url.pathname)) return;
    event.preventDefault();
    broadcastWipe("security-exit");
    await clearPwaData(config.appType);
    navigator.serviceWorker?.controller?.postMessage({ type: "PWA_PURGE_PRIVATE" });
    window.location.assign(url.href);
  }, true);

  document.addEventListener("submit", async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    let url;
    try {
      url = new URL(form.action || window.location.href, window.location.href);
    } catch (error) {
      return;
    }
    if (!isLogoutPath(url.pathname)) return;
    event.preventDefault();
    broadcastWipe("security-exit");
    await clearPwaData(config.appType);
    navigator.serviceWorker?.controller?.postMessage({ type: "PWA_PURGE_PRIVATE" });
    form.submit();
  }, true);
};

const broadcastWipe = (reason) => {
  if ("BroadcastChannel" in window) {
    const channel = new BroadcastChannel("ceoweb-pwa");
    channel.postMessage({ type: "context-wiped", reason, source: PWA_INSTANCE_ID });
    channel.close();
  }
  try {
    window.localStorage.setItem("ceoweb_pwa_broadcast", JSON.stringify({
      type: "context-wiped",
      reason,
      source: PWA_INSTANCE_ID,
      at: Date.now(),
    }));
  } catch (error) {
    // localStorage is only a fallback for browsers without BroadcastChannel.
  }
};

const bindCrossTabWipe = (config) => {
  const handle = async (message) => {
    if (message?.type !== "context-wiped" || message.source === PWA_INSTANCE_ID) return;
    await clearPwaData(config.appType);
    window.dispatchEvent(new CustomEvent("ceoweb:pwa:reauth-required"));
  };

  if ("BroadcastChannel" in window) {
    const channel = new BroadcastChannel("ceoweb-pwa");
    channel.addEventListener("message", (event) => handle(event.data));
  }

  window.addEventListener("storage", (event) => {
    if (event.key !== "ceoweb_pwa_broadcast" || !event.newValue) return;
    try {
      handle(JSON.parse(event.newValue));
    } catch (error) {
      // Ignore malformed cross-tab messages.
    }
  });
};

export const initPwa = async () => {
  const config = readConfig();
  const referralInstall = hasReferralInstallIntent();
  if (!config && !referralInstall) return;

  const effectiveConfig = config || {
    enabled: true,
    killSwitch: false,
    appType: "user",
    serviceWorkerUrl: "/pwa/sw.js",
    scope: "/",
    bootstrapUrl: "",
    snapshotUrl: "",
    telemetryUrl: "",
    versionUrl: "/api/pwa/version",
  };
  if (referralInstall) ensureUserManifest();

  const sendTelemetry = (event, detail) => telemetry(effectiveConfig, event, detail);
  const ui = createPwaUi(effectiveConfig, sendTelemetry);
  if (config) {
    bindCrossTabWipe(config);
    purgeBeforeSecurityExit(config);
  }

  bindInstallPrompt(
    (capability) => ui.showInstall(capability),
    (outcome) => {
      clearReferralInstallIntent();
      sendTelemetry(outcome === "accepted" ? "install_accepted" : "install_dismissed", {
        app_type: effectiveConfig.appType,
      });
    }
  );
  window.addEventListener("ceoweb:pwa:install-result", clearReferralInstallIntent);

  if (!effectiveConfig.killSwitch) {
    try {
      await registerServiceWorker(effectiveConfig, ({ apply }) => ui.showUpdate(apply));
      sendTelemetry("sw_registered", { app_type: effectiveConfig.appType });
    } catch (error) {
      sendTelemetry("sw_registration_failed", {
        app_type: effectiveConfig.appType,
        reason: String(error?.message || "registration_failed").slice(0, 100),
      });
    }
  }

  if (isStandalone()) {
    clearReferralInstallIntent();
    sendTelemetry("standalone_launch", { app_type: effectiveConfig.appType });
  }

  if (!config) return;

  try {
    const context = await bootstrapAndSync(config);
    initWebPush(context, sendTelemetry);
  } catch (error) {
    sendTelemetry("snapshot_sync_failed", {
      resource: "bootstrap",
      reason: String(error?.message || "bootstrap_failed").slice(0, 100),
    });
  }

  window.addEventListener("online", () => {
    bootstrapAndSync(config).catch(() => {});
  });

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      bootstrapAndSync(config).catch(() => {});
    }
  });
};

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => initPwa(), { once: true });
} else {
  initPwa();
}
